import React from 'react';
import PropTypes from "prop-types";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {
    countriesConstants,
    statesConstants,
    membershipUtils,
    membershipConstants,
    datesConstants
} from "mindsets-js-sdk";

import UserRoles from "./UserRoles";
import NumberLessonsCreated from "./NumberLessonsCreated";
import DistrictSubscription from "./DistrictSubscription";
import SchoolSubscription from "./SchoolSubscription";
import School from "./School";
import EditSchool from "./EditSchool";
import NumberLessonsJoinedAsStudent from "./NumberLessonsJoinedAsStudent";
import NumberLessonsJoinedAsCoTeacher from "./NumberLessonsJoinedAsCoTeacher";
import Edit from "./Edit";
import ResetPassword from "./ResetPassword";
import InvalidateSession from "./InvalidateSession";

function Row({user, row_number, online}) {
    const curricula = useSelector(state => state.users_curricula);

    function renderGrowthButton(user) {
        if (user.persona === 'student' && user.streams_count > 0) {
            return (
                <Link className='button' to={'/student/' + user.id + '/growth'}>Growth</Link>
            );
        }

        return null;
    }

    function renderLessonSchools() {
        if (Array.isArray(user.streams) && user.streams.length > 0) {
            const schools = [];
            user.streams.map(stream => {
                schools.push(
                    <div key={stream.school_id}>
                        <EditSchool school={{id: stream.school_id, name: stream.school_name}}/>
                    </div>
                );
            });

            return schools;
        }

        return null;
    }

    function renderStateMismatch() {
        if (user.school && user.school.state && user.state && user.school.state !== user.state) {
            return (
                <div className='text-danger'>
                    State of the user({user.state}) does not match the state of the School ({user.school.state})
                </div>
            );
        }

        return null;
    }

    function renderCountryMismatch() {
        if (user.school && user.school.country && user.country && user.school.country !== user.country) {
            return (
                <div className='text-danger'>
                    Country of the user({user.country}) does not match the country of the School ({user.school.country})
                </div>
            );
        }

        return null;
    }

    function renderCurriculumMismatch() {
        if (user.curriculum_id && user.school && user.school.curriculum_id && user.school.curriculum_id !== user.curriculum_id) {
            return (
                <div className='text-danger'>
                    Curriculum of the user({curricula.find(curriculum => curriculum.id === user.curriculum_id).name}) does not match the curriculum of the School ({curricula.find(curriculum => curriculum.id === user.school.curriculum_id).name})
                </div>
            );
        }

        return null;
    }

    function renderCountry() {
        if (user.country && Object.keys(countriesConstants.COUNTRIES).includes(user.country)) {
            return (
                <div>{countriesConstants.COUNTRIES[user.country]}</div>
            );
        }

        return null;
    }

    function renderState() {
        if (user.country && user.state && Object.keys(countriesConstants.COUNTRIES).includes(user.country) && Object.keys(statesConstants.STATES).includes(user.country) && Object.keys(statesConstants.STATES[user.country]).includes(user.state)) {
            return (
                <div>{statesConstants.STATES[user.country][user.state]}</div>
            );
        }

        return null;
    }

    return (
        <tr key={user.id}>
            <td>{row_number}</td>
            <td>{user.id} {online ? <i className="fas fa-dot-circle green"/> : null}</td>
            <td>
                <div>{user.fname ? user.fname : ''} {user.lname ? user.lname : ''} {user.grade_level ? ("(Grade " + user.grade_level + ")") : ''}</div>
                <div>{user.email}</div>
                {renderCountry()}
                {renderState()}
                {renderCountryMismatch()}
                {renderStateMismatch()}
                {renderCurriculumMismatch()}
                <School user={user}/>
            </td>
            <td>{user.curriculum_id ? curricula.find(curriculum => curriculum.id === user.curriculum_id).name : 'N/A'}</td>
            <td>
                <UserRoles roles={user.roles} persona={user.persona}/>
            </td>
            <td className="text-center"><NumberLessonsCreated user={user}/></td>
            <td>
                <div>Joined
                    on: {new Date(user.created_at * 1000).toLocaleDateString('en-US', datesConstants.US_DATE_FORMAT)}
                    <hr/>
                </div>
                <div>Using: {user.remote_provider}
                    <hr/>
                </div>
                <DistrictSubscription user={user}/>
                <SchoolSubscription user={user}/>
                {membershipConstants.PLAN_FREE === membershipUtils.getPlanType(user) ? <div>Free account</div> : null}
                {user.teacher_subscribed === 1 ? <div>Paid subscription</div> : null}
            </td>
            <td>{renderLessonSchools()}</td>
            <td className="text-center"><NumberLessonsJoinedAsStudent user={user}/></td>
            <td className="text-center"><NumberLessonsJoinedAsCoTeacher user={user}/></td>
            <td>
                <Edit user={user}/>
                {renderGrowthButton(user)}
                <ResetPassword user={user}/>
                <InvalidateSession user={user}/>
            </td>
        </tr>
    );
}

Row.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        email: PropTypes.string.isRequired,
        fname: PropTypes.string,
        lname: PropTypes.string,
        persona: PropTypes.string.isRequired,
        roles: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            initial: PropTypes.bool.isRequired,
        })).isRequired,
        number_lessons: PropTypes.number.isRequired,
        country: PropTypes.string,
        curriculum_id: PropTypes.number,
        school: PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            subscriptions: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number.isRequired,
                starts_at: PropTypes.number.isRequired,
                ends_at: PropTypes.number.isRequired
            })),
            district: PropTypes.shape({
                id: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                subscription: PropTypes.arrayOf(PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    starts_at: PropTypes.number.isRequired,
                    ends_at: PropTypes.number.isRequired
                }))
            }),
            curriculum_id: PropTypes.number,
        }),
        streams: PropTypes.arrayOf(PropTypes.shape({
            school_id: PropTypes.number.isRequired,
            school_name: PropTypes.string.isRequired
        })),
        streams_count: PropTypes.number.isRequired,
        co_classes_count: PropTypes.number.isRequired,
        created_at: PropTypes.number.isRequired,
        remote_provider: PropTypes.string,
        grade_level: PropTypes.string
    }).isRequired,
    row_number: PropTypes.number.isRequired,
    online: PropTypes.bool.isRequired
};

export default Row;
